const DEFAULT_RETRY_CONFIG = {
    maxAttempts: 3,
    baseDelayMs: 1000,
    shouldBackoff: false,
    backoffBase: 2
};

export class MaxRetriesReachedError extends Error {
    constructor(message: string, public readonly result: any) {
        super(message);
    }
}

export async function withRetry<T = void>(
    action: () => Promise<T>,
    retryConfig: {
        checkRetryCondition: (error: Error, response: T) => boolean,
        onBeforeRetry?: (error: Error, response: T, retriesRemaining: number) => Promise<void>,
        maxAttempts?: number,
        baseDelayMs?: number,
        shouldBackoff?: boolean,
        backoffBase?: number
    }
): Promise<T> {
    const {
        checkRetryCondition, onBeforeRetry, maxAttempts, baseDelayMs, shouldBackoff, backoffBase
    } = { ...DEFAULT_RETRY_CONFIG, ...(retryConfig ?? {}) };

    let attempt = 1;

    const execute = async () => {
        let error = null;
        let response = null;

        try {
            response = await action();
        } catch (err) {
            error = err;
        }

        const shouldRetry = checkRetryCondition(error, response);
        return { error, response, shouldRetry };
    };

    const waitBeforeRetry = async () => {
        const backoffDelayMs = baseDelayMs * backoffBase ** (attempt - 1);
        // Add jitter on top of the exponential delay so concurrent callers hitting the
        // same rate/concurrency limit (e.g. multiple widget instances for one user) don't
        // retry in lockstep and re-trigger the same limit together.
        const delayMs = shouldBackoff ? backoffDelayMs + (Math.random() * backoffDelayMs * 0.5) : baseDelayMs;
        await new Promise(resolve => setTimeout(resolve, delayMs));
    };

    while (attempt <= maxAttempts) {
        const { error, response, shouldRetry } = await execute();

        if (attempt === maxAttempts && shouldRetry) {
            if (error) {
                throw error;
            }
            throw new MaxRetriesReachedError(`Max retries reached after ${maxAttempts} attempts`, response);
        }

        if (attempt === maxAttempts || !shouldRetry) {
            if (error) {
                throw error;
            }
            return response;
        }

        await waitBeforeRetry();
        if (onBeforeRetry) {
            await onBeforeRetry(error, response, maxAttempts - attempt);
        }
        attempt++;
    }
}
